import React, { useCallback } from 'react';
import { Layers } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { DataPoint, PercentagePoint } from './components/DataPoint';
import { useTranslation } from 'react-i18next';

// Colors for the chart
const COLORS = {
  preventive: '#10b981', // Green
  reactive: '#3b82f6',   // Blue (matches Created vs. Resolved chart)
  other: '#6b7280',      // Gray
};

/**
 * Summary row displaying Preventive, Reactive, Other counts and preventive percentage
 */
const SummaryRow = ({
  preventiveCount,
  reactiveCount,
  otherCount,
  preventivePercentage,
  onPreventiveClick,
  onReactiveClick,
  onOtherClick
}) => {
  const { t } = useTranslation();
  return (
  <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
    <DataPoint
      value={preventiveCount}
      label={t('shared.preventive', 'Preventive')}
      color={COLORS.preventive}
      onClick={onPreventiveClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={reactiveCount}
      label={t('shared.reactive', 'Reactive')}
      color={COLORS.reactive}
      onClick={onReactiveClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={otherCount}
      label={t('shared.other', 'Other')}
      color={COLORS.other}
      onClick={onOtherClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <PercentagePoint
      value={preventivePercentage.toFixed(1)}
      label={t('reporting.work_orders_by_type.ratio', 'Preventive Ratio')}
    />
  </div>
);
}

/**
 * WorkOrdersByTypeWidget - Displays work order distribution by type
 *
 * Shows:
 * - Summary counts for preventive, reactive, and other work orders
 * - Preventive percentage
 * - Stacked bar chart showing trends over time
 */
const WorkOrdersByTypeWidget = ({
  data,
  loading,
  error,
  onPreventiveClick,
  onReactiveClick,
  onOtherClick,
  onChartBarClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary, chartData } = data || {};
  const {
    preventiveCount = 0,
    reactiveCount = 0,
    otherCount = 0,
    preventivePercentage = 0
  } = summary || {};
  const isEmpty = !chartData || chartData.length === 0;

  // Handle bar click
  const handleBarClick = useCallback((data, type) => {
    if (onChartBarClick && data && data.payload) {
      onChartBarClick(data.payload, type);
    }
  }, [onChartBarClick]);

  return (
    <ReportWidget
      title={t('shared.work_orders_by_type', 'Work Orders by Type')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={Layers}
      emptyMessage={t('shared.no_data_available', 'No data available for the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        preventiveCount={preventiveCount}
        reactiveCount={reactiveCount}
        otherCount={otherCount}
        preventivePercentage={preventivePercentage}
        onPreventiveClick={onPreventiveClick}
        onReactiveClick={onReactiveClick}
        onOtherClick={onOtherClick}
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="periodLabel"
            stroke="#666"
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#666" tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar
            dataKey="otherCount"
            stackId="a"
            fill={COLORS.other}
            name={t('shared.other', 'Other')}
            cursor="pointer"
            onClick={(data) => handleBarClick(data, 'Other')}
          />
          <Bar
            dataKey="preventiveCount"
            stackId="a"
            fill={COLORS.preventive}
            name={t('shared.preventive', 'Preventive')}
            cursor="pointer"
            onClick={(data) => handleBarClick(data, 'Preventive')}
          />
          <Bar
            dataKey="reactiveCount"
            stackId="a"
            fill={COLORS.reactive}
            name={t('shared.reactive', 'Reactive')}
            cursor="pointer"
            onClick={(data) => handleBarClick(data, 'Reactive')}
          />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default WorkOrdersByTypeWidget;
